import { mongoIdFromValue } from '../../../utils/characterInstanceMongoId'

/**
 * The player's un-acknowledged roster intent, kept separately from server state.
 *
 * Everything before this tried to answer "may this server payload overwrite the UI?" — generation
 * counters, refetch stamps, a time window, then `emittedAt`. Each closed one hole and opened the
 * next, because the question is unanswerable: when the remove's reply lands, it is genuinely the
 * newest thing the server has said *and* it genuinely reports an empty slot, since the follow-up
 * assign has not been sent yet. The payload is not stale relative to other payloads. It is stale
 * relative to the player's intent, and intent was nowhere in the data model.
 *
 * So the roster is derived instead of stored:
 *
 *     displayed = replayRosterOps(pendingOps, serverRoster)
 *
 * Server state is then free to be overwritten by anything, in any order, with no guards at all —
 * it is not what is on screen. An operation leaves the queue only when the server acknowledges it
 * (folded into the server roster at the same instant, so nothing flickers) or rejects it (dropped,
 * and its effect simply stops being replayed).
 */
/**
 * The inventory row backing a slot. Endpoints hand back slightly different character shapes and the
 * building screens read a wide set of optional fields off it (`_id`, `stats`, `rarity`, …), so it
 * stays an open record rather than a closed interface.
 */
export type RosterCharacterData = Record<string, any>

export type PendingRosterOp = {
  /** Client-generated; the acknowledgement refers back to it. */
  id: string
  building: string
  castleId: number
  characterId: string
} & (
  | { kind: 'remove' }
  /** `data` is the inventory row, captured at enqueue time so the slot renders like a fetched one. */
  | { kind: 'add'; order: number; data: RosterCharacterData }
)

export type RosterSlot = {
  order: number
  data: RosterCharacterData
  /**
   * Hospital slots carry the server's heal-tick timestamp, which the screen projects live HP from.
   * Absent on a slot the client just added — no heal has been ticked for it yet, so the projection
   * correctly falls back to the server's `currentHp`.
   */
  addedAt?: string | Date | number | null
}

let opCounter = 0

export function nextRosterOpId(): string {
  opCounter += 1
  return `roster-op-${opCounter}`
}

function slotId(slot: unknown): string {
  const data = (slot as { data?: { _id?: unknown; id?: unknown } })?.data
  return mongoIdFromValue(data?._id ?? data?.id)
}

export function opTargets(op: PendingRosterOp, building: string, castleId: number): boolean {
  return op.building === building && op.castleId === castleId
}

/** Apply one operation to a roster, returning a new array. */
export function applyRosterOp(roster: RosterSlot[], op: PendingRosterOp): RosterSlot[] {
  const id = mongoIdFromValue(op.characterId)
  if (!id) return roster

  if (op.kind === 'remove') {
    return roster.filter((slot) => slotId(slot) !== id)
  }

  // An add displaces whatever shares its id or its slot, matching how the server keys a roster.
  return roster
    .filter((slot) => slotId(slot) !== id && slot.order !== op.order)
    .concat([{ order: op.order, data: op.data }])
}

/** Replay every pending operation for one building on top of the server's roster. */
export function replayRosterOps(
  ops: readonly PendingRosterOp[],
  serverRoster: readonly RosterSlot[],
  building: string,
  castleId: number,
): RosterSlot[] {
  let roster = serverRoster as RosterSlot[]
  let touched = false
  for (const op of ops) {
    if (!opTargets(op, building, castleId)) continue
    roster = applyRosterOp(roster, op)
    touched = true
  }
  // Identity is preserved when nothing applies, so selectors don't churn.
  return touched ? roster : (serverRoster as RosterSlot[])
}
